import React, { useEffect, useState, useRef, useCallback } from 'react';
import { screeningAPI } from '@/services/api';
import { ClipboardList, Play, CheckCircle, Clock, ArrowLeft, Gauge, MessageSquare, Star, Mic, MicOff, Square, Video, RotateCcw, ShieldCheck, AlertTriangle } from 'lucide-react';
import Pagination, { usePagination } from '@/components/shared/Pagination';

const TIMER_OPTIONS = [3, 4, 5];
const WARMUP_QUESTION = 'Tell me about yourself in 2 minutes. (This is a mic & camera check — your answer is not scored.)';
const WARMUP_MAX_SEC = 120;
const ANSWER_MAX_SEC = 60;

export default function ApplicantScreenings() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [detailView, setDetailView] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [recording, setRecording] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [audioBlobs, setAudioBlobs] = useState({});
  const [answerTexts] = useState({});
  // Anti-cheat enforcement state
  const [prepSeconds, setPrepSeconds] = useState(5); // configurable 3-5s, default 5
  const [phase, setPhase] = useState('idle'); // idle | warmup | warmup-recording | warmup-review | questions
  const [warmupBlobUrl, setWarmupBlobUrl] = useState('');
  const [permissionError, setPermissionError] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const canvasRef = useRef(null);
  const previewVideoRef = useRef(null);
  const reviewVideoRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const recordingTargetRef = useRef('question'); // 'warmup' | 'question'
  const sPg = usePagination(sessions, 20);

  useEffect(() => { screeningAPI.getMyScreenings().then(setSessions).catch(() => { /* noop */ }); }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (warmupBlobUrl) URL.revokeObjectURL(warmupBlobUrl);
  }, [warmupBlobUrl]);

  const startScreening = async (session) => {
    try {
      if (session.status === 'scheduled') await screeningAPI.startSession(session.id);
      const s = await screeningAPI.getSession(session.id);
      setActive(s);
      setCurrentQ(0);
      setAudioBlobs({});
      setPermissionError('');
      setWarmupBlobUrl('');
      // First show warmup mic/camera check
      setPhase('warmup');
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const startCountdown = () => {
    setCountdown(prepSeconds);
    setRecording(false);
  };

  // Configurable countdown then auto-start recording
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => {
      if (countdown === 1) {
        setCountdown(0);
        beginRecording();
      } else {
        setCountdown(countdown - 1);
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const beginRecording = async () => {
    try {
      setPermissionError('');
      // Always request video + audio (anti-cheat: visible candidate)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: { width: 640, height: 480 } });
      streamRef.current = stream;

      // Attach to preview video element
      const previewEl = recordingTargetRef.current === 'warmup' ? previewVideoRef.current : previewVideoRef.current;
      if (previewEl) { previewEl.srcObject = stream; previewEl.muted = true; try { await previewEl.play(); } catch { /* ignore */ } }

      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : (MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : 'audio/webm');
      const mediaRecorder = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mime.startsWith('video') ? 'video/webm' : 'audio/webm' });
        if (recordingTargetRef.current === 'warmup') {
          const url = URL.createObjectURL(blob);
          setWarmupBlobUrl(url);
          setPhase('warmup-review');
        } else {
          setAudioBlobs(prev => ({ ...prev, [currentQ]: blob }));
        }
        // Stop preview tracks once stopped (release camera)
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
      };

      // Setup waveform visualization
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      source.connect(analyser);
      analyserRef.current = analyser;

      mediaRecorder.start(100);
      setRecording(true);
      const maxSec = recordingTargetRef.current === 'warmup' ? WARMUP_MAX_SEC : ANSWER_MAX_SEC;
      setRecordTime(maxSec);
      if (recordingTargetRef.current === 'warmup') setPhase('warmup-recording');
      drawWaveform();

      timerRef.current = setInterval(() => {
        setRecordTime(prev => {
          if (prev <= 1) {
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (err) {
      const msg = err?.name === 'NotAllowedError' || err?.name === 'SecurityError'
        ? 'Camera & microphone permission denied. Please enable both in your browser address bar — this interview round is voice & video only.'
        : err?.name === 'NotFoundError'
          ? 'No camera or microphone detected. Connect them and try again.'
          : 'Could not access camera/microphone. Please check your devices and permissions.';
      setPermissionError(msg);
      setRecording(false);
      setCountdown(0);
      // If warmup, stay on warmup phase so user can retry
      if (recordingTargetRef.current === 'warmup') setPhase('warmup');
    }
  };

  const drawWaveform = () => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext('2d');
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const barWidth = (canvas.width / bufferLength) * 1.5;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height * 0.9;
        const hue = 210 + (i / bufferLength) * 30;
        ctx.fillStyle = `hsla(${hue}, 80%, 60%, 0.9)`;
        ctx.fillRect(x, canvas.height - barHeight, barWidth - 1, barHeight);
        x += barWidth;
      }
    };
    draw();
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  }, []);

  const startWarmup = () => {
    setPermissionError('');
    recordingTargetRef.current = 'warmup';
    startCountdown();
  };

  const retryWarmup = () => {
    if (warmupBlobUrl) { URL.revokeObjectURL(warmupBlobUrl); setWarmupBlobUrl(''); }
    setPhase('warmup');
  };

  const confirmWarmupAndStartQuestions = () => {
    if (warmupBlobUrl) { URL.revokeObjectURL(warmupBlobUrl); setWarmupBlobUrl(''); }
    recordingTargetRef.current = 'question';
    setPhase('questions');
    startCountdown();
  };

  const submitAnswer = async () => {
    if (!active) return;
    const blob = audioBlobs[currentQ];
    if (!blob) { alert('Please record your answer before submitting.'); return; }
    setSubmitting(true);
    try {
      // Voice/Video-only round: upload media → backend transcribes via Whisper → answer_text persisted
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      const fd = new FormData();
      fd.append('question_index', String(currentQ));
      const ext = blob.type.startsWith('video') ? 'webm' : 'webm';
      fd.append('media', blob, `q${currentQ}.${ext}`);
      const resp = await fetch(`${API_URL}/api/screening/${active.id}/upload-answer`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }

      if (currentQ < active.questions.length - 1) {
        setCurrentQ(currentQ + 1);
        recordingTargetRef.current = 'question';
        startCountdown();
      }
    } catch (e) { alert(e.message); }
    setSubmitting(false);
  };

  const completeScreening = async () => {
    if (!active) return;
    setSubmitting(true);
    try {
      await screeningAPI.completeSession(active.id);
      setActive(null);
      setSessions(await screeningAPI.getMyScreenings());
    } catch (e) { alert(e.message); }
    setSubmitting(false);
  };

  const viewDetail = async (session) => {
    try { setDetailView(await screeningAPI.getSession(session.id)); } catch (e) { alert(e.message); }
  };

  // ===== DETAIL VIEW =====
  if (detailView) {
    const ev = detailView.overall_evaluation;
    const scoreColor = (s) => s >= 70 ? 'text-emerald-600 dark:text-emerald-400' : s >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';
    const recBadge = { proceed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20', hold: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20', reject: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' };
    return (
      <div data-testid="screening-detail-view">
        <button onClick={() => setDetailView(null)} className="flex items-center gap-1.5 text-slate-500 dark:text-zinc-500 text-sm mb-4 hover:text-slate-600 dark:hover:text-zinc-300" data-testid="back-to-screenings"><ArrowLeft className="w-4 h-4" /> Back</button>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">{detailView.job_title || 'Screening Results'}</h1>
        <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Completed {detailView.completed_at ? new Date(detailView.completed_at).toLocaleDateString() : ''}</p>
        {ev && (
          <>
            <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-6 mb-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex flex-col items-center">
                  <div className="relative w-28 h-28">
                    <svg className="w-28 h-28 -rotate-90" viewBox="0 0 120 120"><circle cx="60" cy="60" r="52" fill="none" stroke="#27272a" strokeWidth="8" /><circle cx="60" cy="60" r="52" fill="none" stroke={ev.overall_score >= 70 ? '#10b981' : ev.overall_score >= 50 ? '#f59e0b' : '#ef4444'} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${(ev.overall_score/100)*327} 327`} /></svg>
                    <div className="absolute inset-0 flex items-center justify-center"><span className={`text-3xl font-bold ${scoreColor(ev.overall_score)}`}>{ev.overall_score}</span></div>
                  </div>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs mt-2">Overall Score</p>
                </div>
                <div className="flex-1 space-y-3 w-full">
                  <ScoreBar label="Functional" score={ev.functional_score} />
                  <ScoreBar label="Technical" score={ev.technical_score} />
                  <ScoreBar label="Behavioral" score={ev.behavioral_score} />
                </div>
                {ev.recommendation && <div className="flex flex-col items-center gap-2"><span className={`text-xs px-3 py-1.5 rounded-full border capitalize font-medium ${recBadge[ev.recommendation] || 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 border-slate-300 dark:border-zinc-700'}`}>{ev.recommendation}</span><p className="text-slate-500 dark:text-zinc-600 text-xs">AI Recommendation</p></div>}
              </div>
            </div>
            {ev.summary && <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5 mb-6"><div className="flex items-center gap-2 mb-2"><MessageSquare className="w-4 h-4 text-slate-500 dark:text-zinc-400" /><h3 className="text-slate-900 dark:text-white font-medium text-sm">AI Assessment</h3></div><p className="text-slate-500 dark:text-zinc-400 text-sm leading-relaxed">{ev.summary}</p></div>}
          </>
        )}
        <h3 className="text-slate-900 dark:text-white font-medium mb-3">Questions & Answers</h3>
        <div className="space-y-3">
          {detailView.questions?.map((q, i) => {
            const qEval = ev?.question_evaluations?.find(e => e.question_index === i);
            return (
              <div key={i} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5" data-testid={`question-detail-${i}`}>
                <div className="flex items-center justify-between mb-2"><span className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 capitalize">{q.category}</span>{qEval?.score != null && <span className={`text-sm font-semibold ${scoreColor(qEval.score)}`}>{qEval.score}/100</span>}</div>
                <p className="text-slate-900 dark:text-white text-sm mb-3">{q.question}</p>
                <div className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800/30 rounded-lg p-3 mb-2"><p className="text-slate-600 dark:text-zinc-300 text-sm">{q.answer_text || <span className="text-slate-500 dark:text-zinc-600 italic">Audio answer</span>}</p></div>
                {qEval?.feedback && <div className="flex items-start gap-2 mt-2"><Star className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-500 mt-0.5 flex-shrink-0" /><p className="text-slate-500 dark:text-zinc-500 text-xs">{qEval.feedback}</p></div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ===== ACTIVE SCREENING — WARM-UP CHECK + VOICE/VIDEO RECORDING =====
  if (active) {
    const q = active.questions[currentQ];
    const isLast = currentQ === active.questions.length - 1;
    const hasRecording = !!audioBlobs[currentQ];

    // --- WARM-UP PHASE: mic & camera check ---
    if (phase === 'warmup' || phase === 'warmup-recording' || phase === 'warmup-review') {
      return (
        <div data-testid="screening-warmup">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Mic & Camera Check</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">{active.job_title} — verify your devices before the AI interview starts.</p>

          <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-4 mb-5 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-amber-700 dark:text-amber-300 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-100">
              <p className="font-medium mb-1">This is a voice & video round.</p>
              <p className="text-amber-200/80 text-xs leading-relaxed">Camera and microphone are required. You cannot type answers. We&apos;ll start with a warm-up question so you can confirm everything works.</p>
            </div>
          </div>

          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-6">
            <span className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 mb-3 inline-block">warm-up · not scored</span>
            <p className="text-slate-900 dark:text-white text-lg mb-6" data-testid="warmup-question-text">{WARMUP_QUESTION}</p>

            {phase === 'warmup' && countdown === 0 && !warmupBlobUrl && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="text-xs text-slate-500 dark:text-zinc-500 uppercase tracking-wide">Thinking time before recording</label>
                  <select
                    value={prepSeconds}
                    onChange={(e) => setPrepSeconds(Number(e.target.value))}
                    className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-1.5 text-sm text-slate-900 dark:text-white"
                    data-testid="prep-seconds-select"
                  >
                    {TIMER_OPTIONS.map(n => <option key={n} value={n}>{n} seconds</option>)}
                  </select>
                </div>
                {permissionError && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-200 text-sm rounded-lg p-3 flex items-start gap-2" data-testid="permission-error">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{permissionError}</span>
                  </div>
                )}
                <div className="text-center py-2">
                  <button onClick={startWarmup} className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg mx-auto hover:bg-blue-500 font-medium" data-testid="begin-mic-check-btn">
                    <Video className="w-5 h-5" /> Begin Mic &amp; Camera Check
                  </button>
                </div>
              </div>
            )}

            {countdown > 0 && (
              <div className="flex flex-col items-center py-8" data-testid="warmup-countdown">
                <p className="text-slate-500 dark:text-zinc-500 text-sm mb-3">Get ready to answer in…</p>
                <div className="text-6xl font-bold text-blue-600 dark:text-blue-400 animate-pulse">{countdown}</div>
                <p className="text-slate-500 dark:text-zinc-600 text-xs mt-3">Camera & microphone will start automatically</p>
              </div>
            )}

            {phase === 'warmup-recording' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-red-600 dark:text-red-400 text-sm font-medium">Recording (warm-up)</span>
                  </div>
                  <span className="text-slate-500 dark:text-zinc-400 text-sm font-mono">{recordTime}s left</span>
                </div>
                <div className="bg-slate-50 dark:bg-[#0a0a0b] rounded-lg p-3">
                  <video ref={previewVideoRef} autoPlay muted playsInline className="w-full max-w-md rounded mx-auto" data-testid="warmup-video-preview" />
                </div>
                <div className="bg-slate-50 dark:bg-[#0a0a0b] rounded-lg p-3">
                  <canvas ref={canvasRef} width={500} height={60} className="w-full h-[60px] rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-slate-500 dark:text-zinc-500 text-xs">Speak naturally for up to 2 minutes. Verify you can see yourself.</p>
                  <button onClick={stopRecording} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-500" data-testid="stop-warmup-btn">
                    <Square className="w-4 h-4" /> Stop
                  </button>
                </div>
              </div>
            )}

            {phase === 'warmup-review' && warmupBlobUrl && (
              <div className="space-y-4" data-testid="warmup-review">
                <div className="bg-slate-50 dark:bg-[#0a0a0b] rounded-lg p-3">
                  <video ref={reviewVideoRef} src={warmupBlobUrl} controls className="w-full max-w-md rounded mx-auto" data-testid="warmup-playback" />
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <div>
                    <p className="text-emerald-700 dark:text-emerald-300 text-sm font-medium">Warm-up recorded</p>
                    <p className="text-slate-500 dark:text-zinc-500 text-xs">Play it back. If your face is visible and you can hear yourself clearly, start the interview.</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button onClick={retryWarmup} className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-200 rounded-lg text-sm hover:bg-slate-200 dark:hover:bg-zinc-700" data-testid="retry-warmup-btn">
                    <RotateCcw className="w-4 h-4" /> Retry mic check
                  </button>
                  <button onClick={confirmWarmupAndStartQuestions} className="flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-500" data-testid="start-interview-btn">
                    <CheckCircle className="w-4 h-4" /> Camera & voice work — start interview
                  </button>
                </div>
              </div>
            )}
          </div>
          <button onClick={() => { stopRecording(); if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); setActive(null); setPhase('idle'); }} className="mt-4 text-slate-500 dark:text-zinc-500 text-sm hover:text-slate-600 dark:hover:text-zinc-300" data-testid="exit-screening-warmup">Exit screening</button>
        </div>
      );
    }

    // --- ACTUAL QUESTIONS PHASE ---
    return (
      <div data-testid="screening-active">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Screening: {active.job_title}</h1>
        <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Question {currentQ + 1} of {active.questions.length} · <span className="text-amber-300/80">Voice & video only</span></p>

        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-6">
          <span className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 mb-3 inline-block">{q?.category}</span>
          <p className="text-slate-900 dark:text-white text-lg mb-6">{q?.question}</p>

          {/* Countdown */}
          {countdown > 0 && (
            <div className="flex flex-col items-center py-8" data-testid="question-countdown">
              <p className="text-slate-500 dark:text-zinc-500 text-sm mb-3">Think for a moment…</p>
              <div className="text-6xl font-bold text-blue-600 dark:text-blue-400 animate-pulse">{countdown}</div>
              <p className="text-slate-500 dark:text-zinc-600 text-xs mt-3">Recording starts automatically in {countdown}s</p>
            </div>
          )}

          {/* Recording active */}
          {countdown === 0 && recording && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-red-600 dark:text-red-400 text-sm font-medium">Recording</span>
                </div>
                <span className="text-slate-500 dark:text-zinc-400 text-sm font-mono">{recordTime}s left</span>
              </div>

              <div className="bg-slate-50 dark:bg-[#0a0a0b] rounded-lg p-3">
                <video ref={previewVideoRef} autoPlay muted playsInline className="w-full max-w-md rounded mx-auto" data-testid="question-video-preview" />
              </div>

              <div className="bg-slate-50 dark:bg-[#0a0a0b] rounded-lg p-3">
                <canvas ref={canvasRef} width={500} height={60} className="w-full h-[60px] rounded" />
              </div>

              <div className="flex items-center justify-between">
                <p className="text-slate-500 dark:text-zinc-500 text-xs">Speak clearly. Max {ANSWER_MAX_SEC} seconds per answer.</p>
                <button onClick={stopRecording} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-500" data-testid="stop-recording-btn">
                  <Square className="w-4 h-4" /> Stop Recording
                </button>
              </div>
            </div>
          )}

          {/* After recording / answer review */}
          {countdown === 0 && !recording && (
            <div className="space-y-4">
              {permissionError && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-200 text-sm rounded-lg p-3 flex items-start gap-2" data-testid="permission-error-question">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{permissionError}</span>
                </div>
              )}
              {hasRecording ? (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <div>
                    <p className="text-emerald-700 dark:text-emerald-300 text-sm font-medium">Answer recorded!</p>
                    <p className="text-slate-500 dark:text-zinc-500 text-xs">Recording saved. You can re-record or move to the next question.</p>
                  </div>
                  <button onClick={() => { setAudioBlobs(prev => { const n = {...prev}; delete n[currentQ]; return n; }); recordingTargetRef.current = 'question'; startCountdown(); }}
                    className="ml-auto text-xs text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white px-3 py-1.5 bg-slate-100 dark:bg-zinc-800 rounded-lg" data-testid="re-record-btn">Re-record</button>
                </div>
              ) : (
                <div className="text-center py-4">
                  <button onClick={() => { recordingTargetRef.current = 'question'; startCountdown(); }} className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg mx-auto hover:bg-blue-500" data-testid="start-recording-btn">
                    <Video className="w-5 h-5" /> Start Recording
                  </button>
                </div>
              )}

              <div className="flex gap-3">
                {hasRecording && (
                  <>
                    <button onClick={submitAnswer} disabled={submitting} className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm disabled:opacity-50" data-testid="submit-answer-btn">
                      {submitting ? 'Submitting...' : isLast ? 'Submit Last Answer' : 'Next Question'}
                    </button>
                    {isLast && (
                      <button onClick={async () => { await submitAnswer(); await completeScreening(); }} disabled={submitting} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm" data-testid="complete-screening-btn">
                        <CheckCircle className="w-4 h-4" /> Complete Screening
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        <button onClick={() => { stopRecording(); if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); setActive(null); setPhase('idle'); }} className="mt-4 text-slate-500 dark:text-zinc-500 text-sm hover:text-slate-600 dark:hover:text-zinc-300" data-testid="exit-screening">Exit screening</button>
      </div>
    );
  }

  // ===== SESSIONS LIST =====
  return (
    <div data-testid="applicant-screenings">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Screenings</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Your AI screening sessions</p>
      {sessions.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-10 text-center"><ClipboardList className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" /><p className="text-slate-500 dark:text-zinc-500">No screenings yet</p></div>
      ) : (
        <>
        <div className="space-y-3">
          {sPg.pageItems.map(s => (
            <div key={s.id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-slate-900 dark:text-white font-medium">{s.job_title || 'Screening'}</p>
                <p className="text-slate-500 dark:text-zinc-500 text-xs">{s.questions?.length || 0} questions &middot; <span className="capitalize">{s.status.replace('_', ' ')}</span></p>
              </div>
              <div className="flex items-center gap-2">
                {(s.status === 'scheduled' || s.status === 'in_progress') && (
                  <button onClick={() => startScreening(s)} className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm" data-testid={`start-screening-${s.id}`}><Play className="w-3 h-3" />{s.status === 'in_progress' ? 'Continue' : 'Start'}</button>
                )}
                {s.status === 'completed' && (
                  <div className="flex items-center gap-3">
                    {s.overall_evaluation && <div className="text-right"><p className="text-emerald-600 dark:text-emerald-400 text-sm font-medium">Score: {s.overall_evaluation.overall_score}/100</p></div>}
                    <button onClick={() => viewDetail(s)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm hover:bg-slate-200 dark:hover:bg-zinc-700" data-testid={`view-detail-${s.id}`}><Gauge className="w-3 h-3" /> Details</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <Pagination {...sPg.paginationProps} testIdPrefix="applicant-screenings-pg" />
        </>
      )}
    </div>
  );
}

function ScoreBar({ label, score }) {
  const color = score >= 70 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = score >= 70 ? 'text-emerald-600 dark:text-emerald-400' : score >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';
  return (
    <div><div className="flex justify-between text-sm mb-1"><span className="text-slate-500 dark:text-zinc-400">{label}</span><span className={`font-medium ${textColor}`}>{score}</span></div>
      <div className="h-2 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden"><div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${score}%` }} /></div></div>
  );
}
